import type { ReactNode, Ref } from 'react'
import cx from 'classnames'
import styles from './Sandwich.module.css'

/**
 * Props for Sandwich.
 */
interface SandwichProps {
  children?: ReactNode
  className?: string
  /**
   * Stretches the sandwich to fill the parent container.
   */
  stretch?: boolean
  /**
   * Sandwich to full width
   */
  full?: boolean
}

/**
 * A Sandwich has buns and content. The buns are fixed in size,
 * and the content is flexible.
 */
export function Sandwich({
  children,
  stretch,
  full,
  className,
}: SandwichProps) {
  return (
    <div
      className={cx(
        styles.sandwich,
        stretch && styles.stretch,
        full && styles.full,
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * Props for SandwichBun
 */
export interface SandwichBunProps {
  children?: ReactNode
}

/**
 * Goes on top and/or the bottom of a SandwichContent, but inside Sandwich.
 */
export function SandwichBun({ children }: SandwichBunProps) {
  return <div className={styles.bun}>{children}</div>
}

/**
 * Props for SandwichContent.
 */
export interface SandwichContentProps {
  children?: ReactNode
  /**
   * Stretch the inner elements of the content by making the
   * <SandwichContent> a flex container similar to the <Sandwich> itself.
   * This is useful when you want a single child element of the content to
   * occupy the remaining space.
   */
  stretch?: boolean
  ref?: Ref<HTMLDivElement>
}

/**
 * The content of the Sandwich. Fills the available space.
 */
export function SandwichContent({
  children,
  stretch,
  ref,
}: SandwichContentProps) {
  return (
    <div ref={ref} className={cx(styles.content, stretch && styles.stretch)}>
      {children}
    </div>
  )
}
